import {
  DeleteOutlineIcon,
  MoreVerticalOutlineIcon,
  VerifiedOutlineIcon,
} from '@carboncopyco/icons';
import type { TourTheme } from '@carboncopyco/types';
import { ColorHex, Dropdown, DropdownItem, TextBase } from '@carboncopyco/ui';
import classNames from 'classnames';
import type { ReactNode } from 'react';

type RgbaColorObject = {
  r: string | number;
  g: string | number;
  b: string | number;
  a: string | number;
};

const rgbaObjectToCssValue = (color: RgbaColorObject) => {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
};

const ThemeColorCircle = ({ color }: { color: RgbaColorObject }) => {
  return (
    <div
      className="w-4 h-4 ml-2 border border-ds-natural-150 rounded-full"
      style={{ background: rgbaObjectToCssValue(color) }}
    />
  );
};

const TourThemeItemOptions = ({ onDelete }: { onDelete: () => void }) => {
  return (
    <Dropdown
      side="right"
      asChild
      triggerElement={
        <div
          className="absolute right-0 top-0 bottom-0 flex justify-end align-center rounded-r-lg w-14 px-1.5 invisible group-hover:visible hover:bg-ds-natural-150"
          style={{
            background:
              'linear-gradient(270deg, white 50%, rgba(255, 255, 255, 0) 100%)',
          }}
        >
          <MoreVerticalOutlineIcon />
        </div>
      }
    >
      <div className="w-40">
        <DropdownItem
          onClick={onDelete}
          label="Delete"
          iconLeft={<DeleteOutlineIcon />}
        />
      </div>
    </Dropdown>
  );
};

export type TourThemeItemProps = {
  tourTheme: TourTheme;
  onClick?: (theme: TourTheme) => void;
  onDelete?: (theme: TourTheme) => void;
  isActive?: boolean;
  className?: string;
  rightIcon?: ReactNode;
  showMoreOptionsDropdown?: boolean;
};

export const TourThemeItem = ({
  tourTheme,
  isActive = false,
  className,
  rightIcon,
  onClick,
  onDelete,
  showMoreOptionsDropdown,
}: TourThemeItemProps) => {
  const backgroundColor =
    tourTheme.tooltipConfig.balloon.colorAndShape.backgroundColor;
  const buttonColor =
    tourTheme.tooltipConfig.buttons.nextButton.colorAndShape.backgroundColor;
  const titleColor = tourTheme.tooltipConfig.text.title.textColor;

  return (
    <div
      className={classNames(
        'flex p-2 mb-2 border border-ds-natural-150 rounded-lg justify-between items-center cursor-pointer relative group',
        {
          'border-ds-primary': isActive,
        },
        className
      )}
      onClick={() => onClick?.(tourTheme)}
    >
      <TextBase
        label={tourTheme.name}
        color={isActive ? 'text-ds-primary' : 'text-ds-natural-600'}
        ellipsis
      />
      <div className="flex">
        {isActive && (
          <VerifiedOutlineIcon
            size={16}
            color={ColorHex.accent2}
          />
        )}
        <ThemeColorCircle color={backgroundColor} />
        <ThemeColorCircle color={buttonColor} />
        <ThemeColorCircle color={titleColor} />
        {rightIcon && <div className="ml-2">{rightIcon}</div>}
        {showMoreOptionsDropdown && (
          <>
            {onDelete && (
              <TourThemeItemOptions onDelete={() => onDelete(tourTheme)} />
            )}
          </>
        )}
      </div>
    </div>
  );
};
